import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { MarkytShell } from "./components/markyt/MarkytShell";
import { requireAdmin, requireAuth, requireContributor } from "./lib/route-guards";
import { categoryFromSlug } from "./lib/skills/categories";
import { discoverySearchSchema } from "./lib/skills/search-params";
import { RootLayout } from "./routes/__root";
import { AllSkillsPage } from "./routes/all-skills";
import { AnalyticsPage } from "./routes/analytics";
import { ItemsPage } from "./routes/dashboard/items";
import { ItemDetailPage } from "./routes/dashboard/items/detail";
import { SettingsEmailPage } from "./routes/dashboard/settings/email";
import { SettingsSystemPage } from "./routes/dashboard/settings/system";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { DiscoveryPage } from "./routes/discovery";
import { LoginPage } from "./routes/login";
import { ModerationPage } from "./routes/moderation";
import { MySkillsPage } from "./routes/my-skills";
import { SignupPage } from "./routes/signup";
import { SkillDetailPage } from "./routes/skill-detail";
import { SubmitPage } from "./routes/submit";

const rootRoute = createRootRoute({ component: RootLayout });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});
const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

// Authenticated Markyt shell at the root.
function MarkytShellRoute() {
  return (
    <MarkytShell>
      <Outlet />
    </MarkytShell>
  );
}

const markytLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: "markyt",
  beforeLoad: requireAuth,
  component: MarkytShellRoute,
});

const discoveryRoute = createRoute({
  getParentRoute: () => markytLayout,
  path: "/",
  component: DiscoveryPage,
});
const allSkillsRoute = createRoute({
  getParentRoute: () => markytLayout,
  path: "/skills",
  validateSearch: (s) => discoverySearchSchema.parse(s),
  component: AllSkillsPage,
});
// Legacy category URLs fold into All Skills (DELTA §1): /category/$cat → /skills?cat=$cat.
const categoryRedirectRoute = createRoute({
  getParentRoute: () => markytLayout,
  path: "/category/$cat",
  beforeLoad: ({ params }) => {
    const cat = categoryFromSlug(params.cat);
    throw redirect({ to: "/skills", search: cat ? { cat } : {} });
  },
});
const skillDetailRoute = createRoute({
  getParentRoute: () => markytLayout,
  path: "/skill/$id",
  component: SkillDetailPage,
});
const submitRoute = createRoute({
  getParentRoute: () => markytLayout,
  path: "/submit",
  beforeLoad: requireContributor,
  component: SubmitPage,
});
const mySkillsRoute = createRoute({
  getParentRoute: () => markytLayout,
  path: "/my-skills",
  beforeLoad: requireContributor,
  component: MySkillsPage,
});
const moderationRoute = createRoute({
  getParentRoute: () => markytLayout,
  path: "/moderation",
  beforeLoad: requireAdmin,
  component: ModerationPage,
});
const analyticsRoute = createRoute({
  getParentRoute: () => markytLayout,
  path: "/analytics",
  beforeLoad: requireAdmin,
  component: AnalyticsPage,
});

// Admin-only pages (/dashboard/*) render inside the full Markyt shell so the
// left nav panel persists — same chrome as the rest of the app.
const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: requireAdmin,
  component: MarkytShellRoute,
});

const itemsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/items",
  component: ItemsPage,
});
const itemDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/items/$itemId",
  component: ItemDetailPage,
});
const settingsUsersRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings/users",
  component: SettingsUsersPage,
});
const settingsSystemRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings/system",
  component: SettingsSystemPage,
});
const settingsEmailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings/email",
  component: SettingsEmailPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  signupRoute,
  markytLayout.addChildren([
    discoveryRoute,
    allSkillsRoute,
    categoryRedirectRoute,
    skillDetailRoute,
    submitRoute,
    mySkillsRoute,
    moderationRoute,
    analyticsRoute,
  ]),
  dashboardLayout.addChildren([
    itemsRoute,
    itemDetailRoute,
    settingsUsersRoute,
    settingsSystemRoute,
    settingsEmailRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
